// 产品数据标准化
import { resolveFileUrl, resolveVideoUrl } from '@/components/shared/utils/fileUrl'
import { resolveBodyHtml } from '@/components/shared/utils/resolveBodyHtml'
import { normalizeContentAttachments } from '@/components/shared/utils/contentAttachments'
import { parseShowAttachments, parseShowMedia } from '@/components/shared/utils/contentDisplayFlags'

/** 从正文 JSON 取第一个图文热点的认证图（避免依赖热点整模块） */
function extractCertificationsFromBodyJson(bodyJson) {
  let doc = bodyJson
  if (typeof bodyJson === 'string') {
    try {
      doc = JSON.parse(bodyJson)
    } catch {
      return []
    }
  }
  const urls = []
  const seen = new Set()
  const walk = (node) => {
    if (!node || urls.length) return
    if (node.type === 'hotspotTabs') {
      const list = Array.isArray(node.attrs?.certifications) ? node.attrs.certifications : []
      for (const item of list) {
        const url = typeof item === 'string' ? item.trim() : ''
        if (!url || seen.has(url)) continue
        seen.add(url)
        urls.push(url)
      }
      if (urls.length) return
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) {
        walk(child)
        if (urls.length) return
      }
    }
  }
  walk(doc)
  return urls
}

/** 标准化产品认证图 URL 列表 */
export function normalizeCertificationList(item) {
  const raw =
    item?.certificationList ||
    item?.certifications ||
    item?.certificationUrls ||
    item?.certificationImages
  const fromField = Array.isArray(raw)
    ? raw
      .map((entry) => {
        if (typeof entry === 'string') return resolveFileUrl(entry.trim())
        return resolveFileUrl(String(
          entry?.url || entry?.fileUrl || entry?.iconUrl || entry?.thumbnailUrl || '',
        ).trim())
      })
      .filter(Boolean)
    : []
  if (fromField.length) return fromField
  if (item?.bodyJson) {
    return extractCertificationsFromBodyJson(item.bodyJson)
      .map((url) => resolveFileUrl(url))
      .filter(Boolean)
  }
  return []
}

/** 标准化单条产品媒体资源（图片或视频） */
function normalizeProductMediaItem(media) {
  if (!media) return null
  const rawUrl = media.mediaUrl || media.url || media.fileUrl
  const mediaType = media.mediaType || 'IMAGE'
  const url =
    mediaType === 'VIDEO' ? resolveVideoUrl(rawUrl) : resolveFileUrl(rawUrl)
  if (!url) return null

  return {
    id: media.id,
    mediaType,
    url,
    title: media.title || '',
    sortOrder: Number.isFinite(Number(media.sortOrder)) ? Number(media.sortOrder) : 0
  }
}

/** 标准化产品列表索引中的单条记录 */
export function normalizeProductIndexItem(item) {
  if (!item) return null
  return {
    id: item.id,
    groupId: item.groupId ?? null,
    title: item.title || '',
    image: resolveFileUrl(item.coverUrl),
    coverUrl: item.coverUrl || '',
    categoryId: item.categoryId ?? item.category?.id ?? null,
    categoryName: item.categoryName || item.category?.name || '',
    certifications: normalizeCertificationList({
      ...item,
      certificationList: item?.certificationList || item?.content?.certificationList,
    })
  }
}

/** 批量标准化产品列表索引数据 */
export function normalizeProductIndex(items) {
  return (items || []).map(normalizeProductIndexItem).filter(Boolean)
}

/** 标准化产品详情数据（含图库与媒体列表） */
export function normalizeProductDetail(data) {
  const content = data?.content || {}
  const mediaList = Array.isArray(data?.mediaList) ? data.mediaList : []
  const gallery = []

  const coverUrl = resolveFileUrl(content.coverUrl)
  if (coverUrl) gallery.push(coverUrl)

  const normalizedMediaList = mediaList
    .map(normalizeProductMediaItem)
    .filter(Boolean)
    .sort((a, b) => a.sortOrder - b.sortOrder)

  normalizedMediaList.forEach((media) => {
    if (media.mediaType === 'IMAGE' && media.url && !gallery.includes(media.url)) {
      gallery.push(media.url)
    }
  })

  return {
    id: content.id,
    groupId: content.groupId ?? null,
    locale: content.locale || '',
    title: content.title || '',
    summary: content.summary || '',
    overview: content.summary || '',
    bodyHtml: resolveBodyHtml(content.bodyHtml, content.bodyJson),
    attachments: normalizeContentAttachments(content.attachments),
    showAttachments: parseShowAttachments(content),
    showMedia: parseShowMedia(content),
    categoryId: content.categoryId,
    certifications: normalizeCertificationList({
      ...content,
      certificationList: data?.certificationList || content?.certificationList,
    }),
    coverUrl,
    gallery,
    mediaList: normalizedMediaList
  }
}
